import './FailReportItemForm.scss';

import { useForm } from '@refinedev/antd';
import { Button, Form, Input, Modal } from 'antd';
import { OptionProps } from 'antd/es/select';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import AntdSelect from '@/components/general/AntdSelect/AntdSelect';
import { FailReportItem } from '@/model/FailReport/FailReportItem';
import { PRODUCT_TYPES, ProductTypeStrings } from '@/model/Logistic/PackageItem/ProductTypes';

import useFailReportItemForm from './useFailReportItemForm';

interface FailReportItemFormProps {
  failReportItems: FailReportItem[];
  setFailReportItems: (items: FailReportItem[]) => void;
  closeModal: () => void;
  editingItem?: FailReportItem | null;
  isReadOnly?: boolean;
}

const FailReportItemForm = (props: FailReportItemFormProps) => {
  const { t } = useTranslation();
  const { failReportItems, setFailReportItems, closeModal, editingItem, isReadOnly } = props;
  const { formProps } = useForm<FailReportItem>({
    resource: 'fail_reports_items',
    id: editingItem?.id,
    action: 'edit',
    queryOptions: { enabled: false },
  });
  const { productType, productsSelectProps, serialNumbersSelectProps } = useFailReportItemForm(
    formProps,
    editingItem,
  );
  // Nombre dinámico del campo según el tipo (para unidad o device).
  const elementFormName = useMemo(() => {
    if (productType === PRODUCT_TYPES.UNIT) return 'unidad';
    if (productType === PRODUCT_TYPES.DEVICE) return 'device';
  }, [productType]);
  const elementFormPlaceholder = useMemo(() => {
    if (productType === PRODUCT_TYPES.PRODUCT) return t('Seleccione un producto');
    if (productType === PRODUCT_TYPES.UNIT) return t('Seleccione una unidad');
    if (productType === PRODUCT_TYPES.DEVICE) return t('Seleccione un dispositivo');

    return t('Seleccione un tipo de contenido');
  }, [productType, t]);
  const closeAndReset = useCallback(
    (addMore?: boolean) => {
      if (!addMore) closeModal();
      formProps?.form?.resetFields();
    },
    [closeModal, formProps?.form],
  );
  const onFinish = useCallback(
    (addMore?: boolean) => {
      const values = formProps?.form?.getFieldsValue() as FailReportItem;
      const newItem: FailReportItem = {
        ...values,
        name: formProps?.form?.getFieldValue('name') as string,
      };
      const handleItemAddition = () => {
        setFailReportItems([...failReportItems, newItem]);
        closeAndReset(addMore);
      };

      const handleItemUpdate = () => {
        if (editingItem?.id) {
          newItem.id = editingItem.id;
          newItem.toUpdate = true;
        }

        setFailReportItems(failReportItems.map(item => (item === editingItem ? newItem : item)));
        closeAndReset(addMore);
      };

      // Validación de duplicados: no permitir dos items con el mismo producto/unidad/device.
      const itemWithSameProduct = failReportItems.find(item => {
        if (item !== editingItem && !item.toDelete) {
          if (newItem.product_type === PRODUCT_TYPES.PRODUCT) {
            return item.product === newItem.product;
          } else if (newItem.product_type === PRODUCT_TYPES.UNIT) {
            return (
              item.unidad != undefined &&
              newItem.unidad != undefined &&
              item.unidad === newItem.unidad
            );
          } else if (newItem.product_type === PRODUCT_TYPES.DEVICE) {
            return (
              item.device != undefined &&
              newItem.device != undefined &&
              item.device === newItem.device
            );
          }
        }
      });

      if (itemWithSameProduct) {
        Modal.error({
          title: t('Elemento existente'),
          content: t('Ya existe un item que contiene este elemento'),
        });

        return;
      }

      if (editingItem) {
        handleItemUpdate();
      } else {
        handleItemAddition();
      }
    },
    [formProps?.form, failReportItems, editingItem, setFailReportItems, closeAndReset, t],
  );

  // Modo solo lectura: muestra la información sin inputs editables.
  if (isReadOnly && editingItem) {
    return (
      <div className="FailReportItemForm">
        <div className="FailReportItemFormContent">
          <div className="FailReportItemFormReadOnly">
            <div className="FailReportItemFormReadOnlyRow">
              <span className="FailReportItemFormReadOnlyLabel">{t('Elemento')}:</span>
              <span>{editingItem.name ?? '-'}</span>
            </div>
            <div className="FailReportItemFormReadOnlyRow">
              <span className="FailReportItemFormReadOnlyLabel">{t('Número de serie')}:</span>
              <span>{editingItem.serial_number ?? '-'}</span>
            </div>
            <div className="FailReportItemFormReadOnlyRow">
              <span className="FailReportItemFormReadOnlyLabel">{t('Comentario')}:</span>
              <span>{editingItem.comments ?? '-'}</span>
            </div>
          </div>
        </div>
        <div className="FailReportItemFormButtons">
          <Button onClick={closeModal}>{t('Cerrar')}</Button>
        </div>
      </div>
    );
  }

  return (
    <Form
      {...formProps}
      initialValues={editingItem ?? undefined}
      className="FailReportItemForm"
      onFinish={() => onFinish()}>
      <div className="FailReportItemFormContent">
        <Form.Item label={t('Tipo de contenido')} name="product_type">
          <AntdSelect
            options={Object.values(ProductTypeStrings).map((v, index) => ({
              label: t(v),
              value: index,
            }))}
            disabled={!!editingItem}
            allowClear
          />
        </Form.Item>
        <Form.Item
          label={t('Producto')}
          name="product"
          rules={[{ required: true, message: elementFormPlaceholder }]}>
          <AntdSelect
            {...productsSelectProps}
            disabled={productType === undefined || !!editingItem}
            placeholder={elementFormPlaceholder}
            onChange={(_, opt) =>
              !Array.isArray(opt) &&
              formProps?.form?.setFieldValue('name', opt?.label ?? opt?.value)
            }
            allowClear
            showSearch
          />
        </Form.Item>
        {(productType === PRODUCT_TYPES.UNIT || productType === PRODUCT_TYPES.DEVICE) && (
          <>
            <Form.Item label={t('Número de serie')} name={elementFormName}>
              <AntdSelect
                {...serialNumbersSelectProps}
                placeholder={elementFormPlaceholder}
                allowClear
                showSearch
                onChange={(v, opt) => {
                  serialNumbersSelectProps.onChange?.(v, opt);
                  formProps?.form?.setFieldValue('serial_number', (opt as OptionProps)?.label);
                }}
              />
            </Form.Item>
            <Form.Item hidden name="serial_number">
              <Input />
            </Form.Item>
          </>
        )}
        {/* Campo oculto para persistir el name del producto seleccionado */}
        <Form.Item hidden name="name">
          <Input />
        </Form.Item>

        <Form.Item label={t('Comentario')} name="comments">
          <Input.TextArea rows={3} placeholder={t('Comentario sobre el elemento')} />
        </Form.Item>
      </div>

      <div className="FailReportItemFormButtons">
        <Button onClick={closeModal}>{t('Cancelar')}</Button>
        <Button type="primary" htmlType="submit">
          {editingItem ? t('Actualizar') : t('Guardar')}
        </Button>
        {!editingItem && (
          <Button
            type="primary"
            onClick={() => {
              void formProps.form?.validateFields().then(() => onFinish(true));
            }}>
            {t('Guardar y añadir otro')}
          </Button>
        )}
      </div>
    </Form>
  );
};

export default FailReportItemForm;
